import React, { useEffect, useState } from "react";

import { CloseGreyIcon, CheckWhiteIcon } from "../../../assets/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useNotification } from "../../toster-notification/NotificationContext";
import { useAuth } from "../../../auth/AuthContext";
import UnauthorizedUserModal from "../../shared/UnauthorizedUserModal";
import { ClientOrderInfoDTO } from "../../../models/order/order.response";
import Loader from "../../shared/Loader";
import { orderService } from "../../../services/orderService";
import { handleErrorNotification } from "../../../utils/errorNotificatioMessage";
import { OrderStatusType, orderStatusTypeToClassName, orderStatusTypeToString } from "../../../models/base/OrderStatusType";
import { OrderType, orderTypeToString } from "../../../models/base/OrderType";
import { ToLocalDateTimeString } from "../../../utils/dateFormater";
import { motion } from "framer-motion";
import { connection, startConnection } from "../../../services/signalRConnection";

interface ClientOrderInfoViewProps {
    onClose: () => void;
    orderId: string;
}

const ClientOrderInfoView: React.FC<ClientOrderInfoViewProps> = ({ onClose, orderId }) => {

    const [orderInfo, setOrderInfo] = useState<ClientOrderInfoDTO | null>(null);

    const [loading, setLoading] = useState<boolean>(false);

    const { user } = useAuth();
    const localId = useParams().localId;
    const { notify } = useNotification();
    const navigate = useNavigate();


    useEffect(() => {
        fetchOrderInfo();
    }, [orderId]);

    useEffect(() => {

        const handler = (orderId: string, status: OrderStatusType) => {
            setOrderInfo(prev => prev ? ({ ...prev, currentStatus: status }) as ClientOrderInfoDTO : null)
        }

        const setup = async () => {
            await startConnection();
            connection.on("OrderStatusChange", handler);
        }

        setup();

        return () => {
            connection.off("OrderStatusChange", handler);
        };

    }, [localId]);


    const fetchOrderInfo = async () => {
        if (!localId) {
            notify("error", "Pogrešan lokal ID");
            return;
        }

        if (!orderId) {
            notify("error", "Pogrešan ID porudžbine");
            return;
        }

        setLoading(true);
        try {
            const response = await orderService.getUserOrderInfoInLocal(orderId);

            if (response.success && response.data) {
                setOrderInfo(response.data);
            } else {
                notify("error", response.message || "Greška prilikom učitavanja podataka o porudžbini");
            }
        } catch (error) {
            notify("error", "Greška prilikom učitavanja podataka o porudžbini");
            handleErrorNotification(error, navigate);
        } finally {
            setLoading(false);
        }
    };

    const handleRequestBill = async () => {
        if (!orderInfo || !user) {
            return;
        }

        if (orderInfo.currentStatus !== OrderStatusType.Served) {
            notify("error", "Račun možete zatražiti tek kada porudžbina bude uslužena.");
            return;
        }

        setLoading(true);
        console.log("Requesting bill for order:", orderInfo.id, "by user:", user.id);
        try{
            const response = await orderService.requestBill(orderInfo.id, user.id);

            if (response.success) {
                notify("success", "Račun zatražen. Molimo sačekajte da vam konobar donese račun.");
                orderInfo.currentStatus = OrderStatusType.Bill;
                setOrderInfo({ ...orderInfo });
            } else {
                notify("error", response.message || "Greška prilikom slanja zahteva za račun.");
            }
        }catch(error) {
            notify("error", "Greška prilikom slanja zahteva za račun.");
            handleErrorNotification(error, notify);
        }finally {
            setLoading(false);
        }

    };

    if (!user || !user.id) {
        return <UnauthorizedUserModal
            onClose={onClose}
        />;
    }

    const orderTypeToHtml = (order: ClientOrderInfoDTO) => {
        switch (order.type) {
            case OrderType.DineIn:
                return (
                    <div className="client-modal-order-info-table-div">
                        <div className="description-text">Broj stola</div>
                        <div>{order.tableNumber}</div>
                    </div>
                )
            case OrderType.Pickup:
                return (
                    <div className="client-modal-order-info-table-div">
                        <div className="description-text">Vreme preuzimanja</div>
                        <div>{ToLocalDateTimeString(order.pickUpAt.toString())}</div>
                    </div>
                )
            case OrderType.Delivery:
                return (
                    <div className="client-modal-order-info-table-div">
                        <div className="description-text">Adresa dostave</div>
                        <div>{order.street}, {order.streetNumber}{order.apartment ? `/${order.apartment}` : ""}</div>
                    </div>
                )
        }
    };

    if (loading) {
        return <Loader />;
    }

    if (!orderInfo) {
        return null;
    }

    return (
        <section className="modal-section client-order-info">
            <div className="w-layout-blockcontainer container w-container">
                <motion.section
                    initial={{ opacity: 0, scale: 0.96, y: 12 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.96, y: 12 }}
                    transition={{ duration: 0.2 }}
                >
                    <div className="modal-wrapper">
                        <div className="modal-div">
                            <div className="modal-header-div">
                                <div>
                                    <div className="modal-header">
                                        Porudžbina #{orderInfo.id.slice(0, 8)}<br />
                                    </div>
                                </div>
                                <div className="modal-button-div" onClick={onClose}>
                                    <img loading="lazy" src={CloseGreyIcon} alt="" className="icon-size-20 close" />
                                </div>
                            </div>
                            <div className="modal-main-div">
                                <div className="client-modal-order-info-status-block">
                                    <div className="client-modal-order-info-status-header-div">
                                        <div className="description-text">Status porudžbine</div>
                                        <div className={`small-box-div order-status ${orderStatusTypeToClassName(orderInfo.currentStatus)}`}>
                                            <div>{orderStatusTypeToString(orderInfo.currentStatus)}</div>
                                        </div>
                                    </div>
                                    <div className="client-modal-order-info-status-icon-div">
                                        {orderInfo.currentStatus === OrderStatusType.Ordered || orderInfo.currentStatus === OrderStatusType.Recived || orderInfo.currentStatus === OrderStatusType.Served || orderInfo.currentStatus === OrderStatusType.Paid || orderInfo.currentStatus === OrderStatusType.Bill ? (
                                            <div className="client-modal-order-info-status-icon-block">
                                                <div className="client-modal-order-info-status-icon-photo-div done">
                                                    <img src={CheckWhiteIcon} loading="lazy" alt="" className="icon-size-16" />
                                                </div>
                                                <div className="description-text">Na čekanju</div>
                                            </div>
                                        ) : (
                                            <div className="client-modal-order-info-status-icon-block">
                                                <div className="client-modal-order-info-status-icon-photo-div">
                                                    <div>1</div>
                                                </div>
                                                <div className="description-text">Na čekanju</div>
                                            </div>
                                        )}

                                        {orderInfo.currentStatus === OrderStatusType.Recived || orderInfo.currentStatus === OrderStatusType.Served || orderInfo.currentStatus === OrderStatusType.Paid || orderInfo.currentStatus === OrderStatusType.Bill ? (
                                            <div className="client-modal-order-info-status-icon-block">
                                                <div className="client-modal-order-info-status-icon-photo-div done">
                                                    <img src={CheckWhiteIcon} loading="lazy" alt="" className="icon-size-16" />
                                                </div>
                                                <div className="description-text">Primljeno</div>
                                            </div>
                                        ) : (
                                            <div className="client-modal-order-info-status-icon-block">
                                                <div className="client-modal-order-info-status-icon-photo-div">
                                                    <div>2</div>
                                                </div>
                                                <div className="description-text">Primljeno</div>
                                            </div>
                                        )}

                                        {orderInfo.currentStatus === OrderStatusType.Served || orderInfo.currentStatus === OrderStatusType.Paid || orderInfo.currentStatus === OrderStatusType.Bill ? (
                                            <div className="client-modal-order-info-status-icon-block">
                                                <div className="client-modal-order-info-status-icon-photo-div done">
                                                    <img src={CheckWhiteIcon} loading="lazy" alt="" className="icon-size-16" />
                                                </div>
                                                <div className="description-text">Usluženo</div>
                                            </div>
                                        ) : (
                                            <div className="client-modal-order-info-status-icon-block">
                                                <div className="client-modal-order-info-status-icon-photo-div">
                                                    <div>3</div>
                                                </div>
                                                <div className="description-text">Usluženo</div>
                                            </div>
                                        )}

                                        {orderInfo.currentStatus === OrderStatusType.Paid || orderInfo.currentStatus === OrderStatusType.Bill ? (
                                            <div className="client-modal-order-info-status-icon-block">
                                                <div className="client-modal-order-info-status-icon-photo-div done">
                                                    <img src={CheckWhiteIcon} loading="lazy" alt="" className="icon-size-16" />
                                                </div>
                                                <div className="description-text">Čeka se račun</div>
                                            </div>
                                        ) : (
                                            <div className="client-modal-order-info-status-icon-block">
                                                <div className="client-modal-order-info-status-icon-photo-div">
                                                    <div>4</div>
                                                </div>
                                                <div className="description-text">Čeka se račun</div>
                                            </div>
                                        )}


                                        {orderInfo.currentStatus === OrderStatusType.Paid ? (
                                            <div className="client-modal-order-info-status-icon-block">
                                                <div className="client-modal-order-info-status-icon-photo-div done">
                                                    <img src={CheckWhiteIcon} loading="lazy" alt="" className="icon-size-16" />
                                                </div>
                                                <div className="description-text">Plaćeno</div>
                                            </div>
                                        ) : (
                                            <div className="client-modal-order-info-status-icon-block">
                                                <div className="client-modal-order-info-status-icon-photo-div">
                                                    <div>5</div>
                                                </div>
                                                <div className="description-text">Plaćeno</div>
                                            </div>
                                        )}

                                    </div>
                                    <div className="client-modal-order-info-table-and-time-div">

                                        <div className="client-modal-order-info-time-div">
                                            <div className="description-text">Tip porudžbine</div>
                                            <div>{orderTypeToString(orderInfo.type)}</div>
                                        </div>

                                        {orderTypeToHtml(orderInfo)}

                                        <div className="client-modal-order-info-time-div">
                                            <div className="description-text">Narudžbina kreirana</div>
                                            <div>{ToLocalDateTimeString(orderInfo.createAt && orderInfo.createAt.toString())}</div>
                                        </div>

                                        {orderInfo.isForAnotherPerson && (
                                            <div className="client-modal-order-info-time-div">
                                                <div className="description-text">Porudžbina za</div>
                                                <div>{orderInfo.fullName}</div>
                                            </div>
                                        )}

                                    </div>

                                    {orderInfo.description && (
                                        <div className="client-modal-order-info-notification-div">
                                            <div className="description-text fs-1rem">Napomena:</div>
                                            <div className="input-div">{orderInfo.description}</div>
                                        </div>
                                    )}

                                    <div className="client-modal-order-info-product-block">
                                        <div className="text-block-19">
                                            Stavke porudžbine<br />
                                        </div>
                                        <div className="client-modal-order-info-product-list-div">

                                            {orderInfo.items.map((item, index) => (
                                                <div className="client-modal-order-info-product-div" key={index}>

                                                    {item.productImageUrl && (
                                                        <div className="client-modal-order-info-product-image-div">
                                                            <img src={item.productImageUrl + "@sm.webp"} loading="lazy" alt="" className="client-modal-order-info-product-image" />
                                                        </div>
                                                    )}

                                                    <div className="client-modal-order-info-product-info">

                                                        <div className="client-modal-order-info-product-name">{item.productName}</div>

                                                        <div id="w-node-de73c7ea-97c5-6a84-31c5-e572df64b751-d5e61be0" className="client-modal-order-info-product-quantity description-text">{item.quantity}x</div>

                                                        {item.description ? (
                                                            <div className="client-modal-order-info-product-notification description-text">Napomena: {item.description}</div>
                                                        ) : (
                                                            <div className="client-modal-order-info-product-notification description-text"></div>
                                                        )}

                                                        <div id="w-node-_0ee8be61-5270-7fbe-94ef-5e5c0cafd0e5-d5e61be0" className="client-modal-order-info-product-price client-product-price-div">{item.productVariantPrice * item.quantity} RSD</div>
                                                    </div>
                                                </div>
                                            ))}

                                        </div>
                                    </div>
                                    <div className="client-modal-order-info-total-price input-div">
                                        <div className="text-block-20">Ukupno: </div>
                                        <div className="client-product-price-div fs-1-25rem">{orderInfo.items.reduce((total, item) => total + item.productVariantPrice * item.quantity, 0)} RSD</div>
                                    </div>

                                    {orderInfo.currentStatus === OrderStatusType.Served && (
                                        <div>
                                            <a className="button-primary green-bg w-button" onClick={() => handleRequestBill()}>
                                                Zatraži račun
                                            </a>
                                        </div>
                                    )}

                                </div>
                            </div>
                        </div>
                    </div>
                </motion.section>
            </div>
        </section >
    );

};

export default ClientOrderInfoView;